import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  CreditCard, 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2,
  Plus,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  FileText,
  User,
  Package,
  DollarSign,
  X,
  ShoppingCart,
  Users,
  IndianRupee,
  Building,
  Phone,
  Mail,
  MapPin,
  CreditCard as PaymentIcon,
  ExternalLink,
  Save,
  Upload,
  Eye as EyeIcon
} from 'lucide-react';
import { paymentTransactionsAPI, orderAPI, userAPI, paymentMethodsAPI, paymentOptionsAPI } from '../../services/api';
import { mapPaymentMethodToDisplay, mapPaymentStatusToDisplay, mapOrderStatusToDisplay, getStatusColor } from '../../utils/paymentUtils';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const SplitPaymentsManagement = () => {
  const { notify } = useNotificationHook();
  const [splitPayments, setSplitPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    payment_status: '',
    user_id: '',
    order_id: '',
    search: ''
  });
  
  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Dropdown data states
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchOrder, setSearchOrder] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Payment options configuration state
  const [paymentOptionsConfig, setPaymentOptionsConfig] = useState(null);
  const [loadingPaymentOptions, setLoadingPaymentOptions] = useState(false);

  // Payment methods mapping from database names to display info
  const paymentMethodsMapping = {
    'cashfree': { label: 'Cashfree Payment', icon: CreditCard },
    'razorpay': { label: 'Razorpay Payment', icon: CreditCard },
    'phonepe': { label: 'PhonePe Payment', icon: Phone },
    'offline': { label: 'Offline Payment', icon: Building },
    'cod': { label: 'Cash on Delivery', icon: IndianRupee }
  };
  
  // Form states
  const [formData, setFormData] = useState({
    order_id: '',
    user_id: '',
    transaction_type: 'split_payment',
    amount: '',
    payment_method: '',
    payment_status: 'pending',
    payment_date: '',
     notes: '',
     payment_proof: null,
     original_total_amount: '',
     advance_payment_percentage: '',
     advance_payment_after_demo_percentage: '',
     advance_payment_after_project_percentage: '',
     advance_payment_after_demo_text: 'After Demo Completion',
     advance_payment_after_project_text: 'After Project Completion',
     // New split payment date fields
     after_demo_payment_date: '',
     after_project_payment_date: '',
     after_demo_expiry_date: '',
     after_project_expiry_date: '',
     late_fee_per_day: '500'
   });

  // Fetch payment options configuration
  const fetchPaymentOptionsConfig = async () => {
    try {
      setLoadingPaymentOptions(true);
      const response = await paymentOptionsAPI.getConfig();
      
      if (response?.data?.success) {
        const configData = {};
        Object.keys(response.data.data).forEach(key => {
          configData[key] = response.data.data[key].value;
        });
        setPaymentOptionsConfig(configData);
      }
    } catch (error) {
      console.error('Error fetching payment options config:', error);
    } finally {
      setLoadingPaymentOptions(false);
    }
  };

  // Fetch split payments data
  const fetchSplitPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        transaction_type: 'split_payment',
        ...filters
      };
      
      const response = await paymentTransactionsAPI.getAll(params);
      
      if (response.data && response.data.success) {
        const payments = response.data.data || [];
        console.log('🔍 Split payments data:', payments);
        setSplitPayments(payments);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      } else {
        setError(response.data?.message || 'Failed to fetch split payments');
      }
    } catch (err) {
      console.error('Error fetching split payments:', err);
      setError('Failed to fetch split payments');
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders for dropdown
  const fetchOrders = async (searchTerm = '') => {
    try {
      setLoadingOrders(true);
      const response = await orderAPI.getAll({ search: searchTerm, limit: 10 });
      if (response.data && response.data.success) {
        setOrders(response.data.data.orders || []);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch users for dropdown
  const fetchUsers = async (searchTerm = '') => {
    try {
      setLoadingUsers(true);
      const response = await userAPI.getAll({ search: searchTerm, limit: 10 });
      if (response.data && response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch payment methods
  const fetchPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await paymentMethodsAPI.getActiveMethods();
      if (response.data && response.data.success) {
        // Get the methods array from the correct response structure
        const methods = response.data.data?.methods || [];
        
        // Create display names for each payment method
        const transformedMethods = methods.map(method => {
          let displayName = method;
          switch (method) {
            case 'cashfree':
              displayName = 'Cashfree Payment';
              break;
            case 'razorpay':
              displayName = 'Razorpay Payment';
              break;
            case 'phonepe':
              displayName = 'PhonePe Payment';
              break;
            case 'offline':
              displayName = 'Offline Payment';
              break;
            case 'cod':
              displayName = 'Cash on Delivery';
              break;
            default:
              displayName = method.charAt(0).toUpperCase() + method.slice(1);
          }
          
          return {
            value: method,
            label: displayName
          };
        });
        
        setPaymentMethods(transformedMethods);
        console.log('✅ Payment methods loaded:', transformedMethods);
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Handle order selection
  const handleOrderSelect = (order) => {
    setSelectedOrderDetails(order);
    setFormData(prev => ({
      ...prev,
      order_id: order.id,
      user_id: order.user_id,
      original_total_amount: order.total_amount,
      amount: (order.total_amount * 0.5).toFixed(2) // Default 50% advance
    }));
    setSearchOrder('');
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUserDetails(user);
    setFormData(prev => ({
      ...prev,
      user_id: user.id
    }));
    setSearchUser('');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedFormData = {
        ...prev,
        [name]: value
      };

      // Auto-calculate demo and project payment dates when advance payment date changes
      if (name === 'payment_date' && value) {
        const advanceDate = new Date(value);
        
        // Set After Demo Payment Date to 10 days after advance payment
        const demoDate = new Date(advanceDate);
        demoDate.setDate(demoDate.getDate() + 10);
        updatedFormData.after_demo_payment_date = demoDate.toISOString().slice(0, 16);
        
        // Set After Project Payment Date to 15 days after advance payment
        const projectDate = new Date(advanceDate);
        projectDate.setDate(projectDate.getDate() + 15);
        updatedFormData.after_project_payment_date = projectDate.toISOString().slice(0, 16);
        
        // Set default expiry dates (5 days after respective payment dates)
        const demoExpiryDate = new Date(demoDate);
        demoExpiryDate.setDate(demoExpiryDate.getDate() + 5);
        updatedFormData.after_demo_expiry_date = demoExpiryDate.toISOString().slice(0, 16);
        
        const projectExpiryDate = new Date(projectDate);
        projectExpiryDate.setDate(projectExpiryDate.getDate() + 5);
        updatedFormData.after_project_expiry_date = projectExpiryDate.toISOString().slice(0, 16);
      }

      // Auto-calculate expiry dates when demo payment date changes
      if (name === 'after_demo_payment_date' && value) {
        const demoDate = new Date(value);
        const expiryDate = new Date(demoDate);
        expiryDate.setDate(expiryDate.getDate() + 5);
        updatedFormData.after_demo_expiry_date = expiryDate.toISOString().slice(0, 16);
      }

      // Auto-calculate expiry dates when project payment date changes
      if (name === 'after_project_payment_date' && value) {
        const projectDate = new Date(value);
        const expiryDate = new Date(projectDate);
        expiryDate.setDate(expiryDate.getDate() + 5);
        updatedFormData.after_project_expiry_date = expiryDate.toISOString().slice(0, 16);
      }

      return updatedFormData;
    });
  };

  // Map payment method display name back to database value
  const mapPaymentMethodToDbValue = (displayName) => {
    if (!displayName) return '';
    
    const methodMap = {
      'Cash on Delivery': 'cod',
      'Cash On Delivery': 'cod', // Handle both cases
      'Cashfree Payment': 'cashfree',
      'Razorpay Payment': 'razorpay',
      'PhonePe Payment': 'phonepe',
      'Offline Payment': 'offline'
    };
    
    // First try exact match
    if (methodMap[displayName]) {
      return methodMap[displayName];
    }
    
    // Then try case-insensitive match
    const lowerDisplayName = displayName.toLowerCase();
    for (const [key, value] of Object.entries(methodMap)) {
      if (key.toLowerCase() === lowerDisplayName) {
        return value;
      }
    }
    
    // If still no match, try partial matching
    if (lowerDisplayName.includes('cash') && lowerDisplayName.includes('delivery')) {
      return 'cod';
    }
    if (lowerDisplayName.includes('cashfree')) {
      return 'cashfree';
    }
    if (lowerDisplayName.includes('razorpay')) {
      return 'razorpay';
    }
    if (lowerDisplayName.includes('phonepe')) {
      return 'phonepe';
    }
    if (lowerDisplayName.includes('offline')) {
      return 'offline';
    }
    
    return displayName;
  };
  


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      
      const paymentData = {
        ...formData,
        payment_method: mapPaymentMethodToDbValue(formData.payment_method),
        payment_option_details: JSON.stringify({
          paymentOptionsConfig: {
            advance_payment_percentage: formData.advance_payment_percentage,
            advance_payment_after_demo_percentage: formData.advance_payment_after_demo_percentage,
            advance_payment_after_project_percentage: formData.advance_payment_after_project_percentage,
            advance_payment_after_demo_text: formData.advance_payment_after_demo_text,
            advance_payment_after_project_text: formData.advance_payment_after_project_text,
            // Payment dates
            advance_payment_date: formData.payment_date,
            after_demo_payment_date: formData.after_demo_payment_date,
            after_project_payment_date: formData.after_project_payment_date,
            after_demo_expiry_date: formData.after_demo_expiry_date,
            after_project_expiry_date: formData.after_project_expiry_date,
            late_fee_per_day: formData.late_fee_per_day || '500'
          }
        })
      };

      console.log('🔍 Submitting payment data:', {
        isEdit: !!selectedPayment,
        paymentId: selectedPayment?.id,
        originalFormData: formData,
        processedPaymentData: paymentData
      });

      let response;
      if (selectedPayment) {
        console.log('🔍 Updating existing payment:', selectedPayment.id);
        response = await paymentTransactionsAPI.update(selectedPayment.id, paymentData);
        console.log('🔍 Update response:', response);
      } else {
        console.log('🔍 Creating new payment');
        response = await paymentTransactionsAPI.create(paymentData);
        console.log('🔍 Create response:', response);
      }
      
      if (response.data && response.data.success) {
        setShowPaymentModal(false);
        setShowCreateModal(false);
        setSelectedPayment(null);
        setSelectedOrderDetails(null);
        setSelectedUserDetails(null);
      setFormData({
        order_id: '',
        user_id: '',
        transaction_type: 'split_payment',
        amount: '',
        payment_method: '',
        payment_status: 'pending',
        payment_date: '',
           notes: '',
           payment_proof: null,
           original_total_amount: '',
           advance_payment_percentage: '',
           advance_payment_after_demo_percentage: '',
           advance_payment_after_project_percentage: '',
           advance_payment_after_demo_text: 'After Demo Completion',
           advance_payment_after_project_text: 'After Project Completion',
           // Reset new date fields
           after_demo_payment_date: '',
           after_project_payment_date: '',
           after_demo_expiry_date: '',
           after_project_expiry_date: '',
           late_fee_per_day: '500'
         });
        fetchSplitPayments();
      } else {
        setError(response.data?.message || 'Failed to save split payment');
      }
    } catch (err) {
      console.error('Error saving split payment:', err);
      setError('Failed to save split payment');
    }
  };

  // Handle payment deletion
  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this split payment?')) {
      return;
    }

    try {
      const response = await paymentTransactionsAPI.delete(paymentId);
      if (response.data && response.data.success) {
      fetchSplitPayments();
      } else {
        setError(response.data?.message || 'Failed to delete split payment');
      }
    } catch (err) {
      console.error('Error deleting split payment:', err);
      setError('Failed to delete split payment');
    }
  };

  // Handle view payment details
  const handleViewPayment = async (payment) => {
    console.log('🔍 Opening view modal for payment:', payment);
    setSelectedPayment(payment);
    setShowViewModal(true);
    setViewLoading(true);
    setViewError(null);

    try {
      const response = await paymentTransactionsAPI.getById(payment.id);
      console.log('🔍 View API response:', response);
      
      if (response.data && response.data.success) {
        const paymentData = response.data.data.payment || response.data.data;
        console.log('🔍 Payment data for view:', paymentData);
        setSelectedPayment(paymentData);
      } else {
        console.error('❌ View API error:', response.data);
        setViewError(response.data?.message || 'Failed to load payment details');
      }
    } catch (err) {
      console.error('❌ Error loading payment details:', err);
      setViewError('Failed to load payment details');
    } finally {
      setViewLoading(false);
    }
  };

  // Handle edit payment
  const handleEditPayment = async (payment) => {
    console.log('🔍 Full payment object for edit:', payment);
    setSelectedPayment(payment);
    
    // Parse payment_option_details to extract percentage values
    let paymentOptionDetails = {};
    let config = {};
    
    console.log('🔍 Raw payment_option_details from payment:', payment.payment_option_details);
    console.log('🔍 Type of payment_option_details:', typeof payment.payment_option_details);
    
    // First try to get payment_option_details from the payment object
    if (payment.payment_option_details) {
      try {
        if (typeof payment.payment_option_details === 'string') {
          paymentOptionDetails = JSON.parse(payment.payment_option_details);
          console.log('🔍 Parsed payment_option_details from payment (string):', paymentOptionDetails);
        } else if (typeof payment.payment_option_details === 'object') {
          paymentOptionDetails = payment.payment_option_details;
          console.log('🔍 Using payment_option_details from payment (object):', paymentOptionDetails);
        }
        config = paymentOptionDetails.paymentOptionsConfig || {};
      } catch (err) {
        console.error('Error parsing payment_option_details from payment:', err);
      }
    }
    
    console.log('🔍 Extracted config from payment:', config);
    console.log('🔍 Available config keys from payment:', Object.keys(config));
    
    // If config is empty, try to get values from direct payment fields
    if (Object.keys(config).length === 0) {
      console.log('🔍 Config is empty, checking direct payment fields...');
      console.log('🔍 Direct payment fields:', {
        advance_payment_percentage: payment.advance_payment_percentage,
        advance_payment_after_demo_percentage: payment.advance_payment_after_demo_percentage,
        advance_payment_after_project_percentage: payment.advance_payment_after_project_percentage
      });
    }
    

    
    // Format payment date for datetime-local input
    const formatPaymentDate = (dateString) => {
      if (!dateString) return '';
      try {
        return new Date(dateString).toISOString().slice(0, 16);
      } catch (err) {
        console.error('Error formatting payment date:', err);
        return '';
      }
    };
    
    // Get the best available date (payment_date or created_at)
    const getBestDate = (payment) => {
      if (payment.payment_date) return payment.payment_date;
      if (payment.created_at) return payment.created_at;
      return '';
    };
    
    // Set form data with proper fallbacks from parsed config
    const convertedPaymentMethod = mapPaymentMethodToDbValue(payment.payment_method);
    console.log('🔍 Converting payment method:', {
      original: payment.payment_method,
      converted: convertedPaymentMethod,
      availableMethods: paymentMethods.map(m => m.value)
    });

    // Calculate default dates if they don't exist in config
    const advanceDate = getBestDate(payment);
    let defaultDemoDate = '';
    let defaultProjectDate = '';
    let defaultDemoExpiry = '';
    let defaultProjectExpiry = '';

    if (advanceDate) {
      const advanceDateObj = new Date(advanceDate);
      
      // Calculate default demo payment date (10 days after advance)
      const demoDate = new Date(advanceDateObj);
      demoDate.setDate(demoDate.getDate() + 10);
      defaultDemoDate = demoDate.toISOString().slice(0, 16);
      
      // Calculate default project payment date (15 days after advance)
      const projectDate = new Date(advanceDateObj);
      projectDate.setDate(projectDate.getDate() + 15);
      defaultProjectDate = projectDate.toISOString().slice(0, 16);
      
      // Calculate default expiry dates (5 days after respective payment dates)
      const demoExpiryDate = new Date(demoDate);
      demoExpiryDate.setDate(demoExpiryDate.getDate() + 5);
      defaultDemoExpiry = demoExpiryDate.toISOString().slice(0, 16);
      
      const projectExpiryDate = new Date(projectDate);
      projectExpiryDate.setDate(projectExpiryDate.getDate() + 5);
      defaultProjectExpiry = projectExpiryDate.toISOString().slice(0, 16);
    }
    
    setFormData({
      order_id: payment.order_id || '',
      user_id: payment.user_id || '',
      transaction_type: payment.transaction_type || 'split_payment',
      amount: payment.amount || '',
      payment_method: convertedPaymentMethod || '',
      payment_status: payment.payment_status || 'pending',
      payment_date: formatPaymentDate(getBestDate(payment)) || '',
      notes: payment.notes || '',
      payment_proof: payment.payment_proof || null,
      original_total_amount: payment.original_total_amount || payment.order_total || '',
      advance_payment_percentage: config.advance_payment_percentage || payment.advance_payment_percentage || '50',
      advance_payment_after_demo_percentage: config.advance_payment_after_demo_percentage || payment.advance_payment_after_demo_percentage || '25',
      advance_payment_after_project_percentage: config.advance_payment_after_project_percentage || payment.advance_payment_after_project_percentage || '25',
      advance_payment_after_demo_text: config.advance_payment_after_demo_text || payment.advance_payment_after_demo_text || 'After Demo Completion',
      advance_payment_after_project_text: config.advance_payment_after_project_text || payment.advance_payment_after_project_text || 'After Project Completion',
      // Load date fields from config or use calculated defaults
      after_demo_payment_date: config.after_demo_payment_date || defaultDemoDate,
      after_project_payment_date: config.after_project_payment_date || defaultProjectDate,
      after_demo_expiry_date: config.after_demo_expiry_date || defaultDemoExpiry,
      after_project_expiry_date: config.after_project_expiry_date || defaultProjectExpiry,
      late_fee_per_day: config.late_fee_per_day || '500'
    });
    
    console.log('🔍 Setting form data for edit:', {
      original_payment_method: payment.payment_method,
      converted_payment_method: mapPaymentMethodToDbValue(payment.payment_method),
      payment_method_display: mapPaymentMethodToDisplay(mapPaymentMethodToDbValue(payment.payment_method)),
      available_payment_methods: paymentMethods,
      payment_date: payment.payment_date,
      created_at: payment.created_at,
      best_date: getBestDate(payment),
      formatted_payment_date: formatPaymentDate(getBestDate(payment)),
      advance_payment_percentage: config.advance_payment_percentage || payment.advance_payment_percentage,
      advance_payment_after_demo_percentage: config.advance_payment_after_demo_percentage || payment.advance_payment_after_demo_percentage,
      advance_payment_after_project_percentage: config.advance_payment_after_project_percentage || payment.advance_payment_after_project_percentage,
      config: config,
      // Debug date calculations
      advanceDate: advanceDate,
      defaultDemoDate: defaultDemoDate,
      defaultProjectDate: defaultProjectDate,
      defaultDemoExpiry: defaultDemoExpiry,
      defaultProjectExpiry: defaultProjectExpiry,
      configDates: {
        after_demo_payment_date: config.after_demo_payment_date,
        after_project_payment_date: config.after_project_payment_date,
        after_demo_expiry_date: config.after_demo_expiry_date,
        after_project_expiry_date: config.after_project_expiry_date
      }
    });

    // Fetch and set order details if order_id exists
    if (payment.order_id) {
      try {
        const orderResponse = await orderAPI.getById(payment.order_id);
        if (orderResponse.data && orderResponse.data.success) {
          const orderData = orderResponse.data.data.order || orderResponse.data.data;
          console.log('🔍 Order data fetched:', orderData);
          
          // If config is still empty, try to get payment_option_details from order
          if (Object.keys(config).length === 0 && orderData.payment_option_details) {
            console.log('🔍 Raw payment_option_details from order:', orderData.payment_option_details);
            try {
              let orderPaymentOptionDetails = {};
              if (typeof orderData.payment_option_details === 'string') {
                orderPaymentOptionDetails = JSON.parse(orderData.payment_option_details);
                console.log('🔍 Parsed payment_option_details from order (string):', orderPaymentOptionDetails);
              } else if (typeof orderData.payment_option_details === 'object') {
                orderPaymentOptionDetails = orderData.payment_option_details;
                console.log('🔍 Using payment_option_details from order (object):', orderPaymentOptionDetails);
              }
              
              const orderConfig = orderPaymentOptionDetails.paymentOptionsConfig || {};
              console.log('🔍 Extracted config from order:', orderConfig);
              
              // Update config with order data
              config = orderConfig;
              
              // Update form data with order config values
              setFormData(prev => {
                const updatedFormData = {
                  ...prev,
                  advance_payment_percentage: config.advance_payment_percentage || prev.advance_payment_percentage || '50',
                  advance_payment_after_demo_percentage: config.advance_payment_after_demo_percentage || prev.advance_payment_after_demo_percentage || '25',
                  advance_payment_after_project_percentage: config.advance_payment_after_project_percentage || prev.advance_payment_after_project_percentage || '25',
                  advance_payment_after_demo_text: config.advance_payment_after_demo_text || prev.advance_payment_after_demo_text || 'After Demo Completion',
                  advance_payment_after_project_text: config.advance_payment_after_project_text || prev.advance_payment_after_project_text || 'After Project Completion',
                  // Update date fields from order config
                  after_demo_payment_date: config.after_demo_payment_date || prev.after_demo_payment_date || '',
                  after_project_payment_date: config.after_project_payment_date || prev.after_project_payment_date || '',
                  after_demo_expiry_date: config.after_demo_expiry_date || prev.after_demo_expiry_date || '',
                  after_project_expiry_date: config.after_project_expiry_date || prev.after_project_expiry_date || '',
                  late_fee_per_day: config.late_fee_per_day || prev.late_fee_per_day || '500'
                };
                
                console.log('🔍 Updated form data with order config:', updatedFormData);
                return updatedFormData;
              });
            } catch (err) {
              console.error('Error parsing payment_option_details from order:', err);
            }
          }
          
          setSelectedOrderDetails({
            id: orderData.id,
            order_number: orderData.order_number,
            customer_name: orderData.customer_name || payment.user_name,
            customer_email: orderData.customer_email || payment.user_email,
            total_amount: orderData.total_amount
          });
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        // Fallback to payment data if order fetch fails
        if (payment.order_number) {
          setSelectedOrderDetails({
            id: payment.order_id,
            order_number: payment.order_number,
            customer_name: payment.user_name,
            customer_email: payment.user_email,
            total_amount: payment.order_total || payment.amount
          });
        }
      }
    }

    // Fetch and set user details if user_id exists
    if (payment.user_id) {
      try {
        const userResponse = await userAPI.getById(payment.user_id);
        if (userResponse.data && userResponse.data.success) {
          const userData = userResponse.data.data.user || userResponse.data.data;
          setSelectedUserDetails({
            id: userData.id,
            name: userData.name,
            email: userData.email,
            phone: userData.phone
          });
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        // Fallback to payment data if user fetch fails
        if (payment.user_name || payment.user_email) {
          setSelectedUserDetails({
            id: payment.user_id,
            name: payment.user_name,
            email: payment.user_email,
            phone: payment.user_phone
          });
        }
      }
    }

    setShowPaymentModal(true);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchSplitPayments();
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    if (showPaymentModal || showCreateModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showPaymentModal, showCreateModal]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowPaymentModal(false);
        setShowCreateModal(false);
        setShowViewModal(false);
        setSelectedPayment(null);
        setSelectedOrderDetails(null);
        setSelectedUserDetails(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Debug form data changes
  useEffect(() => {
    if (showPaymentModal && selectedPayment) {
      console.log('🔍 Current form data in modal:', formData);
      console.log('🔍 Payment method in form:', formData.payment_method);
      console.log('🔍 Available payment methods:', paymentMethods);
      console.log('🔍 Payment method matches:', paymentMethods.find(m => m.value === formData.payment_method));
    }
  }, [formData, showPaymentModal, selectedPayment, paymentMethods]);

  useEffect(() => {
    fetchPaymentOptionsConfig();
  }, []);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Split Payments Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage split payment transactions and their details.
          </p>
        </div>
                     <button
             onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
           >
          <Plus className="h-5 w-5" />
          <span>Create Split Payment</span>
           </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filters</h3>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <Filter className="h-4 w-4" />
            <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                placeholder="Search by order number, customer name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Payment Status
              </label>
              <select
                value={filters.payment_status}
                onChange={(e) => handleFilterChange('payment_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="processing">Processing</option>
              </select>
        </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Order ID
              </label>
              <input
                type="text"
                placeholder="Order ID"
                value={filters.order_id}
                onChange={(e) => handleFilterChange('order_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User ID
              </label>
              <input
                type="text"
                placeholder="User ID"
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading split payments...</span>
        </div>
      )}

      {/* Split Payments Table */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Payment ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Order Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {splitPayments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium">No split payments found</p>
                      <p className="text-sm">Create your first split payment to get started.</p>
                  </td>
                </tr>
              ) : (
                splitPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            #{payment.id}
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <div>
                          <p className="font-medium">Order #{payment.order_number || 'N/A'}</p>
                          <p className="text-gray-500 dark:text-gray-400 text-xs">
                            {payment.user_name || payment.user_email || 'N/A'}
                          </p>
                      </div>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        <div>
                          <p className="font-medium">{formatCurrency(payment.amount)}</p>
                          {payment.original_total_amount && (
                            <p className="text-gray-500 dark:text-gray-400 text-xs">
                              of {formatCurrency(payment.original_total_amount)}
                            </p>
                          )}
                      </div>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {mapPaymentMethodToDisplay(payment.payment_method) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.payment_status, 'payment')}`}>
                          {mapPaymentStatusToDisplay(payment.payment_status)}
                        </span>
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(payment.payment_date || payment.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handleViewPayment(payment)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="View Details"
                        >
                            <Eye className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => handleEditPayment(payment)}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                            title="Edit"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
       </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                   <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.total}</span> results
                  </p>
                   </div>
                   <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === pagination.page
                              ? 'z-10 bg-green-50 border-green-500 text-green-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                   </div>
                 </div>
                   </div>
          )}
                   </div>
       )}

       {/* Create/Edit Split Payment Modal */}
       {(showPaymentModal || showCreateModal) && createPortal(
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
             {/* Modal Header */}
             <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                   {selectedPayment ? 'Edit Split Payment' : 'Create Split Payment'}
                 </h2>
                 <button
                   onClick={() => {
                     setShowPaymentModal(false);
                     setShowCreateModal(false);
                     setSelectedPayment(null);
                   }}
                   className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                 >
                   <X className="h-5 w-5" />
                 </button>
                 </div>
                   </div>

             {/* Modal Content */}
             <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto flex-1">
               <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
                 {/* Left Column - Order Selection */}
                 <div className="lg:col-span-1 space-y-6">
                   <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 sm:p-6 rounded-xl border border-blue-200 dark:border-blue-700">
                     <div className="flex items-center space-x-3 mb-4">
                       <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Order Selection</h3>
                     </div>
                     
                     {/* Order Search */}
                     <div className="space-y-3">
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                         Search & Select Order {!selectedOrderDetails && '*'}
                     </label>
                       <div className="relative">
                     <input
                       type="text"
                           placeholder="Search by order number or customer name..."
                           value={searchOrder}
                           onChange={(e) => {
                             setSearchOrder(e.target.value);
                             if (e.target.value.length >= 2) {
                               fetchOrders(e.target.value);
                             }
                           }}
                           className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                       required={!selectedOrderDetails}
                     />
                         <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                   </div>
                       
                       {/* Order Dropdown */}
                       {searchOrder && !selectedOrderDetails && (
                         <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto">
                           {loadingOrders ? (
                             <div className="p-3 text-center text-gray-500">Loading orders...</div>
                           ) : orders.length > 0 ? (
                             orders.map((order) => (
                               <div
                                 key={order.id}
                                 onClick={() => handleOrderSelect(order)}
                                 className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                               >
                                 <div className="flex justify-between items-start">
                   <div>
                                     <p className="font-medium text-gray-900 dark:text-white">#{order.order_number}</p>
                                     <p className="text-sm text-gray-600 dark:text-gray-400">{order.customer_name}</p>
                                     <p className="text-xs text-gray-500">{order.customer_email}</p>
                 </div>
                                   <div className="text-right">
                                     <p className="font-semibold text-blue-600 dark:text-blue-400">
                                       ₹{Number(order.total_amount || 0).toLocaleString()}
                                     </p>
                                     <span className={`text-xs px-2 py-1 rounded-full ${
                                       order.order_status === 'completed' ? 'bg-green-100 text-green-800' :
                                       order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                       'bg-gray-100 text-gray-800'
                                     }`}>
                                       {order.order_status}
                                     </span>
                                   </div>
                                 </div>
                               </div>
                             ))
                           ) : (
                             <div className="p-3 text-center text-gray-500">No orders found</div>
                           )}
                         </div>
                       )}
                       
                       {/* Selected Order Display */}
                       {selectedOrderDetails && (
                         <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                           <div className="flex justify-between items-start">
                   <div>
                               <p className="font-semibold text-blue-900 dark:text-blue-100">#{selectedOrderDetails.order_number}</p>
                               <p className="text-sm text-blue-700 dark:text-blue-300">{selectedOrderDetails.customer_name}</p>
                               <p className="text-xs text-blue-600 dark:text-blue-400">{selectedOrderDetails.customer_email}</p>
                             </div>
                             <div className="text-right">
                               <p className="font-bold text-blue-900 dark:text-blue-100">
                                 ₹{Number(selectedOrderDetails.total_amount || 0).toLocaleString()}
                               </p>
                               <button
                                 type="button"
                                 onClick={() => {
                                   setSelectedOrderDetails(null);
                                   setFormData(prev => ({ ...prev, order_id: '', user_id: '', amount: '' }));
                                   setSearchOrder('');
                                 }}
                                 className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                               >
                                 Change
                               </button>
                   </div>
                   </div>
                 </div>
                       )}
                     </div>
                   </div>

                   {/* User Selection */}
                   <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 sm:p-6 rounded-xl border border-green-200 dark:border-green-700">
                     <div className="flex items-center space-x-3 mb-4">
                       <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Selection</h3>
                     </div>
                     
                     {/* User Search */}
                     <div className="space-y-3">
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                         Search & Select Customer {!selectedUserDetails && '*'}
                     </label>
                       <div className="relative">
                     <input
                           type="text"
                           placeholder="Search by customer name or email..."
                           value={searchUser}
                           onChange={(e) => {
                             setSearchUser(e.target.value);
                             if (e.target.value.length >= 2) {
                               fetchUsers(e.target.value);
                             }
                           }}
                           className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white"
                           required={!selectedUserDetails}
                         />
                         <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                   </div>
                       
                       {/* User Dropdown */}
                       {searchUser && !selectedUserDetails && (
                         <div className="mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto">
                           {loadingUsers ? (
                             <div className="p-3 text-center text-gray-500">Loading users...</div>
                           ) : users.length > 0 ? (
                             users.map((user) => (
                               <div
                                 key={user.id}
                                 onClick={() => handleUserSelect(user)}
                                 className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                               >
                                 <div className="flex justify-between items-start">
                   <div>
                                     <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                                     <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                                     <p className="text-xs text-gray-500">{user.phone}</p>
                 </div>
                                   <div className="text-right">
                                     <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                       ID: {user.id}
                                     </span>
                 </div>
                 </div>
                               </div>
                             ))
                           ) : (
                             <div className="p-3 text-center text-gray-500">No users found</div>
                           )}
                         </div>
                       )}
                       
                       {/* Selected User Display */}
                       {selectedUserDetails && (
                         <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
                           <div className="flex justify-between items-start">
                   <div>
                               <p className="font-semibold text-green-900 dark:text-green-100">{selectedUserDetails.name}</p>
                               <p className="text-sm text-green-700 dark:text-green-300">{selectedUserDetails.email}</p>
                               <p className="text-xs text-green-600 dark:text-green-400">{selectedUserDetails.phone}</p>
                 </div>
                             <div className="text-right">
                               <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                 ID: {selectedUserDetails.id}
                               </span>
                   <button
                     type="button"
                     onClick={() => {
                                   setSelectedUserDetails(null);
                                   setFormData(prev => ({ ...prev, user_id: '' }));
                                   setSearchUser('');
                                 }}
                                 className="block mt-1 text-xs text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                               >
                                 Change
                     </button>
             </div>
           </div>
         </div>
       )}
                     </div>
                   </div>
                 </div>

                 {/* Right Column - Payment Details */}
                 <div className="lg:col-span-2 space-y-6">
                   <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 sm:p-6 rounded-xl border border-purple-200 dark:border-purple-700">
                     <div className="flex items-center space-x-3 mb-6">
                       <PaymentIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                       <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Split Payment Details</h3>
                   </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                       {/* Payment Amount */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Amount *
                     </label>
                         <div className="relative">
                           <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                     <input
                       type="number"
                       name="amount"
                       step="0.01"
                       value={formData.amount}
                       onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="0.00"
                       required
                     />
                   </div>
                 </div>

                       {/* Original Total Amount */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Original Total Amount
                     </label>
                         <div className="relative">
                           <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                     <input
                       type="number"
                             name="original_total_amount"
                       step="0.01"
                             value={formData.original_total_amount}
                       onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="0.00"
                             readOnly
                     />
                   </div>
                 </div>

                       {/* Payment Method */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Method *
                     </label>
                         <div className="relative">
                           <select
                       name="payment_method"
                       value={formData.payment_method}
                       onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white appearance-none"
                       required
                             disabled={loadingPaymentMethods}
                           >
                             <option value="">
                               {loadingPaymentMethods ? 'Loading payment methods...' : 'Select Payment Method'}
                             </option>
                             {paymentMethods.map((method) => (
                               <option key={method.value} value={method.value}>
                                 {method.label}
                               </option>
                             ))}
                             {!loadingPaymentMethods && paymentMethods.length === 0 && (
                               <option value="" disabled>No active payment methods found</option>
                             )}
                           </select>
                           <PaymentIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                           {loadingPaymentMethods ? (
                             <RefreshCw className="absolute right-3 top-3 h-5 w-5 text-gray-400 animate-spin" />
                           ) : (
                             <ChevronDown className="absolute right-3 top-3 h-5 w-5 text-gray-400" />
                           )}
                   </div>
                 </div>

                       {/* Payment Status */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Payment Status
                     </label>
                     <select
                       name="payment_status"
                       value={formData.payment_status}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     >
                       <option value="pending">Pending</option>
                       <option value="completed">Completed</option>
                       <option value="failed">Failed</option>
                       <option value="refunded">Refunded</option>
                           <option value="processing">Processing</option>
                     </select>
                   </div>

                                              {/* Advance Payment Date */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                       Advance Payment Date
                     </label>
                     <input
                           type="datetime-local"
                       name="payment_date"
                       value={formData.payment_date}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     />
                   </div>

                       {/* Advance Payment Percentage */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Advance Payment Percentage
                     </label>
                         <div className="relative">
                     <input
                             type="number"
                             name="advance_payment_percentage"
                             value={formData.advance_payment_percentage}
                       onChange={handleInputChange}
                             className="w-full pr-8 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="50"
                             min="0"
                             max="100"
                     />
                           <span className="absolute right-3 top-3 text-gray-400">%</span>
                   </div>
                       </div>

                       {/* After Demo Percentage */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Demo Percentage
                     </label>
                         <div className="relative">
                     <input
                       type="number"
                             name="advance_payment_after_demo_percentage"
                             value={formData.advance_payment_after_demo_percentage}
                       onChange={handleInputChange}
                             className="w-full pr-8 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="25"
                             min="0"
                             max="100"
                     />
                           <span className="absolute right-3 top-3 text-gray-400">%</span>
                   </div>
                 </div>

                       {/* After Project Percentage */}
                 <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Project Percentage
                   </label>
                         <div className="relative">
                   <input
                     type="number"
                             name="advance_payment_after_project_percentage"
                             value={formData.advance_payment_after_project_percentage}
                     onChange={handleInputChange}
                             className="w-full pr-8 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="25"
                             min="0"
                             max="100"
                   />
                           <span className="absolute right-3 top-3 text-gray-400">%</span>
                 </div>
                 </div>

                       {/* After Demo Payment Date */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Demo Payment Date
                     </label>
                     <input
                           type="datetime-local"
                           name="after_demo_payment_date"
                           value={formData.after_demo_payment_date}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     />
                   </div>

                       {/* After Demo Expiry Date */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Demo Expiry Date
                     </label>
                     <input
                           type="datetime-local"
                           name="after_demo_expiry_date"
                           value={formData.after_demo_expiry_date}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     />
                   </div>

                       {/* After Project Payment Date */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Project Payment Date
                     </label>
                     <input
                           type="datetime-local"
                           name="after_project_payment_date"
                           value={formData.after_project_payment_date}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     />
                   </div>

                       {/* After Project Expiry Date */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           After Project Expiry Date
                     </label>
                     <input
                           type="datetime-local"
                           name="after_project_expiry_date"
                           value={formData.after_project_expiry_date}
                       onChange={handleInputChange}
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                     />
                   </div>

                       {/* Late Fee Per Day (₹) */}
                   <div>
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Late Fee Per Day (₹)
                     </label>
                         <div className="relative">
                           <IndianRupee className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                     <input
                             type="number"
                             name="late_fee_per_day"
                             min="0"
                             step="1"
                             value={formData.late_fee_per_day}
                       onChange={handleInputChange}
                             className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                             placeholder="500"
                     />
                   </div>
                 </div>

                       {/* Notes */}
                       <div className="md:col-span-2">
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Notes
                   </label>
                   <textarea
                     name="notes"
                     value={formData.notes}
                     onChange={handleInputChange}
                     rows="3"
                           className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                           placeholder="Additional notes about this split payment..."
                   />
                 </div>
                     </div>
                   </div>

                   {/* Form Actions */}
                 <div className="flex justify-end space-x-3">
                   <button
                     type="button"
                       onClick={() => {
                         setShowPaymentModal(false);
                         setShowCreateModal(false);
                         setSelectedPayment(null);
                         setSelectedOrderDetails(null);
                         setSelectedUserDetails(null);
                       }}
                       className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                   >
                     Cancel
                   </button>
                   <button
                     type="submit"
                       className="px-6 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                   >
                       {selectedPayment ? 'Update Split Payment' : 'Create Split Payment'}
                   </button>
                 </div>
             </div>
           </div>
             </form>
         </div>
         </div>
       , document.body)}

             {/* Split Payment Details View Modal */}
       {showViewModal && selectedPayment && (() => {
         console.log('🔍 Rendering view modal with payment:', selectedPayment);
         return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
             <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                   Split Payment Details - #{selectedPayment.id}
                   {selectedPayment.order_number && (
                     <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">(Order: {selectedPayment.order_number})</span>
                   )}
                 </h2>
                 <div className="flex items-center space-x-2">
                   <button
                     onClick={() => {
                       setShowViewModal(false);
                       setSelectedPayment(null);
                     }}
                     className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                   >
                     <X className="h-5 w-5" />
                   </button>
                 </div>
               </div>
             </div>

             <div className="p-4 sm:p-6 space-y-6">
               {viewLoading && (
                 <div className="text-center text-gray-600 dark:text-gray-300">Loading payment details...</div>
               )}
               {viewError && (
                 <div className="text-center text-red-600 dark:text-red-400">
                   {viewError}
                   <p className="text-sm mt-2">Showing available data from table...</p>
                     </div>
               )}
               
               

               {/* Payment Summary */}
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Order Status</p>
                   <p className={`text-lg font-semibold ${getStatusColor(selectedPayment.order_status)}`}>
                                             {mapOrderStatusToDisplay(selectedPayment.order_status)}
                   </p>
                     </div>
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Payment Status</p>
                                     <p className={`text-lg font-semibold ${getStatusColor(selectedPayment.payment_status, 'payment')}`}>
                    {mapPaymentStatusToDisplay(selectedPayment.payment_status)}
                  </p>
                     </div>
                 <div>
                   <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Amount</p>
                   <p className="text-lg font-semibold text-gray-900 dark:text-white">
                     {formatCurrency(selectedPayment.amount)}
                   </p>
                   </div>
                     </div>
                     
                     {/* Payment Option Details */}
                     {selectedPayment.payment_option && selectedPayment.payment_option !== 'full' && (
                 <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                   <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3">Payment Option Details</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Type</p>
                       <p className={`text-lg font-semibold ${
                             selectedPayment.payment_option === 'advance' 
                               ? 'text-blue-600 dark:text-blue-400' 
                               : selectedPayment.payment_option === 'emi'
                               ? 'text-purple-600 dark:text-purple-400'
                               : 'text-green-600 dark:text-green-400'
                           }`}>
                             {selectedPayment.payment_option === 'advance' ? 'Split Payment' : 
                              selectedPayment.payment_option === 'emi' ? 'EMI Payment' : 'Full Payment'}
                       </p>
                         </div>
                     <div>
                       <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Payment Method</p>
                       <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                         {mapPaymentMethodToDisplay(selectedPayment.payment_method) || 'N/A'}
                       </p>
                     </div>
                     {selectedPayment.next_payment_amount && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Next Payment Amount</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           ₹{selectedPayment.next_payment_amount}
                       </p>
                           </div>
                         )}
                     {selectedPayment.next_payment_date && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Next Payment Date</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">{formatDate(selectedPayment.next_payment_date)}</p>
                           </div>
                         )}
                     {selectedPayment.remaining_amount && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Remaining Amount</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           ₹{selectedPayment.remaining_amount}
                       </p>
                           </div>
                         )}
                     {selectedPayment.total_installments && (
                       <div>
                         <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Installments</p>
                         <p className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                           {selectedPayment.total_installments}
                       </p>
                     </div>
                     )}

                   </div>
                   
                   {/* Split Payment Instructions */}
                   {selectedPayment.payment_option === 'advance' && (
                     <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-md">
                       <p className="text-sm text-blue-800 dark:text-blue-200">
                         Your split payment plan details are shown below. Please ensure timely payments.
                       </p>
                       
                       {/* Split Payment Details */}
                       {selectedPayment.payment_option_details && (
                         <div className="mt-3 space-y-2">
                           {(() => {
                             // Comprehensive test for split payment #59 - Summary
                             if (selectedPayment.id === 59) {
                               console.log('🔍 Split Payment #59 - COMPREHENSIVE TEST SUMMARY:', {
                                 id: selectedPayment.id,
                                 order_id: selectedPayment.order_id,
                                 amount: selectedPayment.amount,
                                 payment_status: selectedPayment.payment_status,
                                 payment_option: selectedPayment.payment_option,
                                 order_created_at: selectedPayment.order_created_at,
                                 created_at: selectedPayment.created_at,
                                 payment_option_details: selectedPayment.payment_option_details,
                                 paymentOptionsConfig: paymentOptionsConfig
                               });
                             }
                             const details = selectedPayment.payment_option_details;
                             
                             // Helper function to get payment status
                             const getPaymentStatus = (paymentType) => {
                               // Comprehensive test for split payment #59
                               if (selectedPayment.id === 59) {
                                 console.log(`🔍 Split Payment #59 - ${paymentType} Status Check:`, {
                                   payment_status: selectedPayment.payment_status,
                                   after_demo_order_payment_status: selectedPayment.after_demo_order_payment_status,
                                   after_demo_completed: selectedPayment.after_demo_completed,
                                   after_project_order_payment_status: selectedPayment.after_project_order_payment_status,
                                   after_project_completed: selectedPayment.after_project_completed,
                                   order_payment_status: selectedPayment.order_payment_status,
                                   installment_order_payment_status: selectedPayment.installment_order_payment_status
                                 });
                               }
                               
                               if (paymentType === 'advance') {
                                 return selectedPayment.payment_status === 'completed' ? 'Paid' : 'Pending';
                               } else if (paymentType === 'after_demo') {
                                 // For after demo, check if there's a separate order for demo completion
                                 // This would typically be a different order with order_id linked to the original
                                 return selectedPayment.after_demo_order_payment_status === 'completed' || 
                                        selectedPayment.after_demo_completed === true ? 'Paid' : 'Pending';
                               } else if (paymentType === 'after_project') {
                                 // For after project, check if there's a separate order for project completion
                                 return selectedPayment.after_project_order_payment_status === 'completed' || 
                                        selectedPayment.after_project_completed === true ? 'Paid' : 'Pending';
                               }
                               return 'Pending';
                             };

                             // Helper function to get status color
                             const getStatusColor = (status) => {
                               return status === 'Paid' ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400';
                             };

                             // Helper function to get status icon
                             const getStatusIcon = (status) => {
                               return status === 'Paid' ? CheckCircle : Clock;
                             };

                             // Comprehensive test for split payment #59
                             if (selectedPayment.id === 59) {
                               console.log('🔍 Split Payment #59 - Payment Option Details:', {
                                 details_type: typeof details,
                                 details: details,
                                 paymentOptionsConfig: paymentOptionsConfig,
                                 selectedPayment_payment_option_details: selectedPayment.payment_option_details
                               });
                             }
                             
                             // Use payment options config for dynamic text
                             const config = paymentOptionsConfig || {};
                             
                             // Handle string data (try to parse JSON)
                             if (typeof details === 'string') {
                               try {
                                 const parsedDetails = JSON.parse(details);
                                 const storedConfig = parsedDetails.paymentOptionsConfig || {};
                                 
                                 return (
                                   <div className="space-y-2">
                                     {storedConfig.advance_payment_percentage && (
                                       <div className="flex justify-between items-center text-sm">
                                         <span className="text-blue-700 dark:text-blue-300">
                                           {config.advance_payment_now_text || 'Advance (Now)'}:
                                         </span>
                                         <div className="flex items-center space-x-2">
                                           <span className="text-blue-900 dark:text-blue-100 font-medium">
                                             {storedConfig.advance_payment_percentage}%
                                           </span>
                                           <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('advance'))}`}>
                                             ({getPaymentStatus('advance')})
                                           </span>
                                           {React.createElement(getStatusIcon(getPaymentStatus('advance')), { 
                                             className: `w-3 h-3 ${getStatusColor(getPaymentStatus('advance'))}` 
                                           })}
                                         </div>
                                       </div>
                                     )}
                                     {storedConfig.advance_payment_after_demo_percentage && (
                                       <div className="space-y-1">
                                         <div className="flex justify-between items-center text-sm">
                                           <span className="text-blue-700 dark:text-blue-300">
                                             {config.advance_payment_after_demo_text || 'After Demo Completion'}:
                                           </span>
                                           <div className="flex items-center space-x-2">
                                             <span className="text-blue-900 dark:text-blue-100 font-medium">
                                               {storedConfig.advance_payment_after_demo_percentage}%
                                             </span>
                                             <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('after_demo'))}`}>
                                               ({getPaymentStatus('after_demo')})
                                             </span>
                                             {React.createElement(getStatusIcon(getPaymentStatus('after_demo')), { 
                                               className: `w-3 h-3 ${getStatusColor(getPaymentStatus('after_demo'))}` 
                                             })}
                                           </div>
                                         </div>
                                         {/* After Demo Due Date */}
                                         {(() => {
                                           // Comprehensive test for split payment #59
                                           if (selectedPayment.id === 59) {
                                             console.log('🔍 Split Payment #59 - Demo Due Date Calculation:', {
                                               order_created_at: selectedPayment.order_created_at,
                                               created_at: selectedPayment.created_at,
                                               after_demo_payment_date: selectedPayment.after_demo_payment_date,
                                               after_demo_expiry_date: selectedPayment.after_demo_expiry_date,
                                               next_payment_date: selectedPayment.next_payment_date,
                                               due_date: selectedPayment.due_date,
                                               effectiveDueDate: selectedPayment.effectiveDueDate
                                             });
                                           }
                                           
                                           // Get actual payment dates from the split payment configuration
                                           const orderCreatedAt = selectedPayment.order_created_at || selectedPayment.created_at;
                                           
                                           // Get actual payment dates from the split payment configuration
                                           // These should be stored in the payment_option_details or calculated based on business rules
                                           let demoPaymentDate = null;
                                           let demoExpiryDate = null;
                                           
                                           // Check if there are specific dates stored for demo completion
                                           if (selectedPayment.after_demo_payment_date) {
                                             demoPaymentDate = selectedPayment.after_demo_payment_date;
                                           } else if (selectedPayment.after_demo_expiry_date) {
                                             demoExpiryDate = selectedPayment.after_demo_expiry_date;
                                           } else {
                                             // Fallback to calculated dates based on order creation
                                             if (orderCreatedAt) {
                                               const orderDate = new Date(orderCreatedAt);
                                               
                                               // Demo completion typically due 14 days after order
                                               const demoDueDate = new Date(orderDate);
                                               demoDueDate.setDate(demoDueDate.getDate() + 14);
                                               demoPaymentDate = demoDueDate.toISOString().split('T')[0];
                                               
                                               // Demo expiry typically 30 days after order
                                               const demoExpiry = new Date(orderDate);
                                               demoExpiry.setDate(demoExpiry.getDate() + 30);
                                               demoExpiryDate = demoExpiry.toISOString().split('T')[0];
                                             }
                                                                                      }
                                           
                                           // Comprehensive test for split payment #59 - Final Demo Dates
                                           if (selectedPayment.id === 59) {
                                             console.log('🔍 Split Payment #59 - Final Demo Dates:', {
                                               demoPaymentDate,
                                               demoExpiryDate,
                                               finalDate: demoExpiryDate ? demoExpiryDate : demoPaymentDate
                                             });
                                           }
                                           
                                           if (demoPaymentDate || demoExpiryDate) {
                                             return (
                                               <div className="flex justify-between items-center text-xs pl-4">
                                                 <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                                                 <span className="text-gray-800 dark:text-gray-200 font-medium">
                                                   {demoExpiryDate ? 
                                                     formatDate(demoExpiryDate) : 
                                                     demoPaymentDate ? 
                                                       formatDate(demoPaymentDate) : 
                                                       'Not set'
                                                   }
                                                 </span>
                                               </div>
                                             );
                                           }
                                           return null;
                                         })()}
                                       </div>
                                     )}
                                     {storedConfig.advance_payment_after_project_percentage && (
                                       <div className="space-y-1">
                                         <div className="flex justify-between items-center text-sm">
                                           <span className="text-blue-700 dark:text-blue-300">
                                             {config.advance_payment_after_project_text || 'After Project Completion'}:
                                           </span>
                                           <div className="flex items-center space-x-2">
                                             <span className="text-blue-900 dark:text-blue-100 font-medium">
                                               {storedConfig.advance_payment_after_project_percentage}%
                                             </span>
                                             <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('after_project'))}`}>
                                               ({getPaymentStatus('after_project')})
                                             </span>
                                             {React.createElement(getStatusIcon(getPaymentStatus('after_project')), { 
                                               className: `w-3 h-3 ${getStatusColor(getPaymentStatus('after_project'))}` 
                                             })}
                                           </div>
                                         </div>
                                         {/* After Project Due Date */}
                                         {(() => {
                                           // Comprehensive test for split payment #59
                                           if (selectedPayment.id === 59) {
                                             console.log('🔍 Split Payment #59 - Project Due Date Calculation:', {
                                               order_created_at: selectedPayment.order_created_at,
                                               created_at: selectedPayment.created_at,
                                               after_project_payment_date: selectedPayment.after_project_payment_date,
                                               after_project_expiry_date: selectedPayment.after_project_expiry_date,
                                               next_payment_date: selectedPayment.next_payment_date,
                                               due_date: selectedPayment.due_date,
                                               effectiveDueDate: selectedPayment.effectiveDueDate
                                             });
                                           }
                                           
                                           // Get actual payment dates from the split payment configuration
                                           const orderCreatedAt = selectedPayment.order_created_at || selectedPayment.created_at;
                                           
                                           // Get actual payment dates from the split payment configuration
                                           // These should be stored in the payment_option_details or calculated based on business rules
                                           let projectPaymentDate = null;
                                           let projectExpiryDate = null;
                                           
                                           // Check if there are specific dates stored for project completion
                                           if (selectedPayment.after_project_payment_date) {
                                             projectPaymentDate = selectedPayment.after_project_payment_date;
                                           } else if (selectedPayment.after_project_expiry_date) {
                                             projectExpiryDate = selectedPayment.after_project_expiry_date;
                                           } else {
                                             // Fallback to calculated dates based on order creation
                                             if (orderCreatedAt) {
                                               const orderDate = new Date(orderCreatedAt);
                                               
                                               // Project completion typically due 45 days after order
                                               const projectDueDate = new Date(orderDate);
                                               projectDueDate.setDate(projectDueDate.getDate() + 45);
                                               projectPaymentDate = projectDueDate.toISOString().split('T')[0];
                                               
                                               // Project expiry typically 90 days after order
                                               const projectExpiry = new Date(orderDate);
                                               projectExpiry.setDate(projectExpiry.getDate() + 90);
                                               projectExpiryDate = projectExpiry.toISOString().split('T')[0];
                                             }
                                                                                      }
                                           
                                           // Comprehensive test for split payment #59 - Final Project Dates
                                           if (selectedPayment.id === 59) {
                                             console.log('🔍 Split Payment #59 - Final Project Dates:', {
                                               projectPaymentDate,
                                               projectExpiryDate,
                                               finalDate: projectExpiryDate ? projectExpiryDate : projectPaymentDate
                                             });
                                           }
                                           
                                           if (projectPaymentDate || projectExpiryDate) {
                                             return (
                                               <div className="flex justify-between items-center text-xs pl-4">
                                                 <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                                                 <span className="text-gray-800 dark:text-gray-200 font-medium">
                                                   {projectExpiryDate ? 
                                                     formatDate(projectExpiryDate) : 
                                                     projectPaymentDate ? 
                                                       formatDate(projectPaymentDate) : 
                                                       'Not set'
                                                   }
                                                 </span>
                                               </div>
                                             );
                                           }
                                           return null;
                                         })()}
                                       </div>
                                     )}
                                     {storedConfig.advance_payment_instructions && (
                                       <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                         <p className="text-xs text-blue-700 dark:text-blue-300">
                                           {storedConfig.advance_payment_instructions}
                                         </p>
                                       </div>
                                     )}
                                   </div>
                                 );
                               } catch (e) {
                                 return <div className="text-blue-700 dark:text-blue-300 text-sm">Split payment details not available</div>;
                               }
                             }
                             
                             // Handle object data
                             if (typeof details === 'object' && details !== null) {
                               const storedConfig = details.paymentOptionsConfig || {};
                               
                               return (
                                 <div className="space-y-2">
                                   {storedConfig.advance_payment_percentage && (
                                     <div className="flex justify-between items-center text-sm">
                                       <span className="text-blue-700 dark:text-blue-300">
                                         {config.advance_payment_now_text || 'Advance (Now)'}:
                                       </span>
                                       <div className="flex items-center space-x-2">
                                         <span className="text-blue-900 dark:text-blue-100 font-medium">
                                           {storedConfig.advance_payment_percentage}%
                                         </span>
                                         <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('advance'))}`}>
                                           ({getPaymentStatus('advance')})
                                         </span>
                                         {React.createElement(getStatusIcon(getPaymentStatus('advance')), { 
                                           className: `w-3 h-3 ${getStatusColor(getPaymentStatus('advance'))}` 
                                         })}
                                       </div>
                                     </div>
                                   )}
                                   {storedConfig.advance_payment_after_demo_percentage && (
                                     <div className="space-y-1">
                                       <div className="flex justify-between items-center text-sm">
                                         <span className="text-blue-700 dark:text-blue-300">
                                           {config.advance_payment_after_demo_text || 'After Demo Completion'}:
                                         </span>
                                         <div className="flex items-center space-x-2">
                                           <span className="text-blue-900 dark:text-blue-100 font-medium">
                                             {storedConfig.advance_payment_after_demo_percentage}%
                                           </span>
                                           <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('after_demo'))}`}>
                                             ({getPaymentStatus('after_demo')})
                                           </span>
                                           {React.createElement(getStatusIcon(getPaymentStatus('after_demo')), { 
                                             className: `w-3 h-3 ${getStatusColor(getPaymentStatus('after_demo'))}` 
                                           })}
                                         </div>
                                       </div>
                                       {/* After Demo Due Date */}
                                       {(() => {
                                         // Get actual payment dates from the split payment configuration
                                         const demoPaymentDate = details.after_demo_payment_date || 
                                                                details.paymentOptionsConfig?.after_demo_payment_date ||
                                                                selectedPayment.after_demo_payment_date;
                                         
                                         const demoExpiryDate = details.after_demo_expiry_date || 
                                                               details.paymentOptionsConfig?.after_demo_expiry_date ||
                                                               selectedPayment.after_demo_expiry_date;
                                         
                                         if (demoPaymentDate || demoExpiryDate) {
                                           return (
                                             <div className="flex justify-between items-center text-xs pl-4">
                                               <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                                               <span className="text-gray-800 dark:text-gray-200 font-medium">
                                                 {demoExpiryDate ? 
                                                   formatDate(demoExpiryDate) : 
                                                   demoPaymentDate ? 
                                                     formatDate(demoPaymentDate) : 
                                                     'Not set'
                                                 }
                                               </span>
                                             </div>
                                           );
                                         }
                                         return null;
                                       })()}
                                     </div>
                                   )}
                                   {storedConfig.advance_payment_after_project_percentage && (
                                     <div className="space-y-1">
                                       <div className="flex justify-between items-center text-sm">
                                         <span className="text-blue-700 dark:text-blue-300">
                                           {config.advance_payment_after_project_text || 'After Project Completion'}:
                                         </span>
                                         <div className="flex items-center space-x-2">
                                           <span className="text-blue-900 dark:text-blue-100 font-medium">
                                             {storedConfig.advance_payment_after_project_percentage}%
                                           </span>
                                           <span className={`text-xs font-medium ${getStatusColor(getPaymentStatus('after_project'))}`}>
                                             ({getPaymentStatus('after_project')})
                                           </span>
                                           {React.createElement(getStatusIcon(getPaymentStatus('after_project')), { 
                                             className: `w-3 h-3 ${getStatusColor(getPaymentStatus('after_project'))}` 
                                           })}
                                         </div>
                                       </div>
                                       {/* After Project Due Date */}
                                       {(() => {
                                         // Get actual payment dates from the split payment configuration
                                         const projectPaymentDate = details.after_project_payment_date || 
                                                                    details.paymentOptionsConfig?.after_project_payment_date ||
                                                                    selectedPayment.after_project_payment_date;
                                         
                                         const projectExpiryDate = details.after_project_expiry_date || 
                                                                   details.paymentOptionsConfig?.after_project_expiry_date ||
                                                                   selectedPayment.after_project_expiry_date;
                                         
                                         if (projectPaymentDate || projectExpiryDate) {
                                           return (
                                             <div className="flex justify-between items-center text-xs pl-4">
                                               <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                                               <span className="text-gray-800 dark:text-gray-200 font-medium">
                                                 {projectExpiryDate ? 
                                                   formatDate(projectExpiryDate) : 
                                                   projectPaymentDate ? 
                                                     formatDate(projectPaymentDate) : 
                                                     'Not set'
                                                 }
                                               </span>
                                             </div>
                                           );
                                         }
                                         return null;
                                       })()}
                                     </div>
                                   )}
                                   {storedConfig.advance_payment_instructions && (
                                     <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                                       <p className="text-xs text-blue-700 dark:text-blue-300">
                                         {storedConfig.advance_payment_instructions}
                                       </p>
                                     </div>
                                   )}
                                 </div>
                               );
                             }
                             
                             return <div className="text-blue-700 dark:text-blue-300 text-sm">Split payment details not available</div>;
                           })()}
                         </div>
                       )}
                   </div>
                   )}
                 </div>
               )}

               {/* Customer Information */}
                   <div>
                 <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Name</p>
                     <p className="text-gray-900 dark:text-white">{selectedPayment.user_name || 'N/A'}</p>
                   </div>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email</p>
                     <p className="text-gray-900 dark:text-white">{selectedPayment.user_email || 'N/A'}</p>
                   </div>
                   {selectedPayment.user_phone && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_phone}</p>
                     </div>
                   )}
                   {selectedPayment.user_whatsapp && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">WhatsApp</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_whatsapp}</p>
                     </div>
                   )}
                   {selectedPayment.user_address && (
                     <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg md:col-span-2">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Address</p>
                       <p className="text-gray-900 dark:text-white">{selectedPayment.user_address}</p>
                     </div>
                   )}
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Order Date</p>
                     <p className="text-gray-900 dark:text-white">{formatDate(selectedPayment.order_created_at || selectedPayment.created_at)}</p>
                   </div>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Payment Method</p>
                     <p className="text-gray-900 dark:text-white">{mapPaymentMethodToDisplay(selectedPayment.payment_method) || 'N/A'}</p>
                   </div>
                 </div>
               </div>

               {/* Order Items */}
               {selectedPayment.items && selectedPayment.items.length > 0 && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Order Items</h3>
                 <div className="space-y-4">
                         {selectedPayment.items.map((item, index) => (
                       <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                         {/* Product Header */}
                         <div className="flex items-center space-x-4 mb-4">
                                 {/* Product Image */}
                           <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                                     {item.product_image ? (
                                       <img 
                                 src={item.product_image.startsWith('http') ? item.product_image : `/uploads/${item.product_image}`}
                                 alt={item.product_name}
                                         className="w-full h-full object-cover"
                                         onError={(e) => {
                                           e.target.style.display = 'none';
                                           e.target.nextSibling.style.display = 'flex';
                                         }}
                                       />
                                     ) : null}
                                     <div className={`w-full h-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center ${item.product_image ? 'hidden' : ''}`}>
                               <Package className="w-8 h-8 text-gray-400" />
                                     </div>
                                     </div>
                           
                           {/* Product Info */}
                           <div className="flex-1">
                             <p className="text-lg font-medium text-gray-900 dark:text-white">{item.product_name}</p>
                             <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                                   </div>
                                     </div>

                         {/* Pricing Details */}
                         <div className="space-y-3">
                                   {/* License Information */}
                                   {item.selected_license && (
                             <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                               <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                 {item.selected_license === 'extended' ? 'Extended License' : 'Regular License'}
                               </span>
                               <div className="text-right">
                                 <span className="font-medium">
                                   ₹{item.selected_license === 'extended' ? 
                                     (item.license_details?.extended_license_price || item.extended_license_price || 0) :
                                     (item.license_details?.regular_license_price || item.regular_license_price || 0)
                                   }
                                 </span>
                                       </div>
                                     </div>
                                   )}
                                   
                                   {/* Extended Support Information */}
                                   {item.extended_support && (
                             <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600">
                               <span className="text-sm font-medium text-green-600 dark:text-green-400">Extended Support</span>
                                     <div className="text-right">
                                       <span className="font-medium">
                                   ₹{item.support_details?.extended_support_price || item.extended_support_price || 0}
                                       </span>
                                     </div>
                                   </div>
                                 )}

                           {/* Addons Information */}
                           {item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                             <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                               <div className="flex justify-between items-center mb-2">
                                 <span className="text-sm font-medium text-purple-600 dark:text-purple-400">Addons</span>
                               </div>
                                   <div className="space-y-1">
                                     {item.selected_addons.map((addonId, addonIndex) => {
                                       const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                       return addon ? (
                                     <div key={addonIndex} className="flex justify-between items-center pl-4">
                                       <span className="text-sm text-gray-600 dark:text-gray-400">{addon.name}</span>
                                           <div className="text-right">
                                         <span className="text-sm font-medium">₹{addon.price || 0}</span>
                                           </div>
                                         </div>
                                       ) : null;
                                     })}
                                   </div>
                               </div>
                           )}

                           {/* Item Total */}
                           <div className="flex justify-between items-center pt-2">
                             <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                             <span className="text-lg font-semibold text-gray-900 dark:text-white">
                               ₹{(() => {
                                 // Calculate total including support and addons
                                 let itemTotal = (item.price || 0) * (item.quantity || 1);
                                 
                                 // Add support price if extended support is selected
                                 if (item.extended_support && (item.support_details?.extended_support_price || item.extended_support_price)) {
                                   itemTotal += (item.support_details?.extended_support_price || item.extended_support_price || 0) * (item.quantity || 1);
                                 }
                                 
                                 // Add addon prices if addons are selected
                                 if (item.selected_addons && Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (item.addon_details || item.addons)) {
                                   item.selected_addons.forEach(addonId => {
                                     const addon = (item.addon_details || item.addons)?.find(a => a.id === addonId);
                                     if (addon && addon.price) {
                                       itemTotal += addon.price * (item.quantity || 1);
                                     }
                                   });
                                 }
                                 
                                 return itemTotal;
                               })()}
                             </span>
                           </div>
                         </div>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               {/* Billing Address */}
               {selectedPayment.billing_address && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Billing Address</h3>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-gray-600 dark:text-gray-400">
                       {typeof selectedPayment.billing_address === 'string' 
                         ? selectedPayment.billing_address 
                         : JSON.stringify(selectedPayment.billing_address, null, 2)
                       }
                     </p>
                   </div>
                 </div>
               )}

               {/* Notes */}
               {(selectedPayment.notes || selectedPayment.order_notes) && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
                   <div className="p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                     <p className="text-gray-600 dark:text-gray-400">
                       {selectedPayment.notes || selectedPayment.order_notes}
                     </p>
                   </div>
                 </div>
               )}

               {/* Rating and Review (if available) */}
               {(selectedPayment.rating || selectedPayment.review) && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Rating & Review</h3>
                   <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                     {selectedPayment.rating && (
                       <div className="mb-2 text-yellow-700 dark:text-yellow-300">
                         Rating: <span className="font-semibold">{selectedPayment.rating}/5</span>
                       </div>
                     )}
                     {selectedPayment.review && (
                       <div className="mb-2">
                         <p className="text-gray-700 dark:text-gray-300 italic">"{selectedPayment.review}"</p>
                       </div>
                     )}
                     {selectedPayment.review_date && (
                       <p className="text-xs text-yellow-700 dark:text-yellow-300">
                         Reviewed on: {new Date(selectedPayment.review_date).toLocaleString()}
                       </p>
                     )}
                   </div>
                 </div>
               )}

               {/* Payment Proof (if available) */}
               {selectedPayment.payment_proof && (
                 <div>
                   <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment Proof</h3>
                   <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                     <div className="flex items-center justify-between">
                       <div className="flex-1">
                         <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                           {selectedPayment.payment_proof.originalName || selectedPayment.payment_proof.filename}
                         </p>
                         <p className="text-xs text-blue-700 dark:text-blue-300">
                           {selectedPayment.payment_proof.size ? `${(selectedPayment.payment_proof.size / 1024).toFixed(1)} KB` : ''} • {selectedPayment.payment_proof.type}
                         </p>
                         {selectedPayment.payment_proof.uploaded_at && (
                           <p className="text-xs text-blue-600 dark:text-blue-400">
                             Uploaded: {new Date(selectedPayment.payment_proof.uploaded_at).toLocaleString()}
                           </p>
                         )}
                       </div>
                       <div className="flex items-center space-x-2">
                           {selectedPayment.payment_proof.type?.startsWith('image/') && (
                             <button
                               onClick={() => {
                                 const imageUrl = `/api/documents/${selectedPayment.payment_proof.filename}`;
                                 window.open(imageUrl, '_blank');
                               }}
                               className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                               title="View Image"
                             >
                             <FileText className="h-4 w-4" />
                             </button>
                           )}
                           {selectedPayment.payment_proof.type === 'application/pdf' && (
                             <button
                               onClick={() => {
                                 const pdfUrl = `/api/documents/${selectedPayment.payment_proof.filename}`;
                                 window.open(pdfUrl, '_blank');
                               }}
                               className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                               title="View PDF"
                             >
                             <FileText className="h-4 w-4" />
                             </button>
                           )}
                           <button
                             onClick={() => {
                               const downloadUrl = `/api/documents/${selectedPayment.payment_proof.filename}/download`;
                               const link = document.createElement('a');
                               link.href = downloadUrl;
                               link.download = selectedPayment.payment_proof.originalName || selectedPayment.payment_proof.filename;
                               link.target = '_blank';
                               document.body.appendChild(link);
                               link.click();
                               document.body.removeChild(link);
                             }}
                             className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                             title="Download File"
                           >
                             <Download className="h-4 w-4" />
                           </button>
                         </div>
                       </div>
                     {selectedPayment.payment_proof.type?.startsWith('image/') && (
                       <div className="mt-4">
                         <img 
                           src={`/api/documents/${selectedPayment.payment_proof.filename}`}
                           alt="Payment Proof"
                           className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                           onError={(e) => { e.target.style.display = 'none'; }}
                         />
                       </div>
                     )}
                     {selectedPayment.payment_proof.type === 'application/pdf' && (
                       <div className="mt-4">
                         <iframe
                           src={`/api/documents/${selectedPayment.payment_proof.filename}#toolbar=0`}
                           className="w-full h-64 rounded-lg border border-gray-200 dark:border-gray-600"
                           title="PDF Preview"
                           onError={(e) => { e.target.style.display = 'none'; }}
                         />
                       </div>
                     )}
                   </div>
                 </div>
               )}
             </div>
           </div>
         </div>
       , document.body);
       })()}
     </div>
   );
 };

export default SplitPaymentsManagement;
